import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { Hash, Search, RotateCcw, Loader } from 'lucide-react'
import api from '../api'

const TYPES = ['A','AAAA','MX','NS','TXT','CNAME','SOA','PTR','SRV']
const TYPE_COLORS = {
  A:'#34d399', AAAA:'#38bdf8', MX:'#fbbf24',
  NS:'#a78bfa', TXT:'#94a3b8', CNAME:'#f59e0b',
  SOA:'#64748b', PTR:'#38bdf8', SRV:'#fbbf24',
}

const QUICK = ['google.com','cloudflare.com','github.com','amazon.com']

export default function DnsLookup() {
  const [domain,  setDomain]  = useState('')
  const [types,   setTypes]   = useState(['A','MX','NS'])
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [queried, setQueried] = useState('')

  const toggle = (t) => setTypes(prev =>
    prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
  )

  const lookup = async () => {
    const d = domain.trim()
    if (!d)            return toast.error('Vui lòng nhập tên miền')
    if (!types.length) return toast.error('Chọn ít nhất một loại bản ghi')
    setLoading(true); setResults([])
    try {
      const res = await api.post('/dns-lookup', { domain: d, types })
      setResults(res.data.results || [])
      setQueried(d)
      if (!res.data.results?.length) toast('Không tìm thấy bản ghi nào', { icon: 'ℹ️' })
      else toast.success(`Tìm thấy ${res.data.results.length} bản ghi`)
    } catch (e) {
      toast.error(e.response?.data?.error || 'Tra cứu thất bại')
    } finally { setLoading(false) }
  }

  const grouped = results.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = []
    acc[r.type].push(r)
    return acc
  }, {})

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Title */}
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ width:40, height:40, borderRadius:10, flexShrink:0,
          background:'linear-gradient(135deg,#0ea5e9,#6366f1)',
          display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Hash size={20} color="#fff" />
        </div>
        <div>
          <h1 style={{ fontSize:18, fontWeight:700 }}>DNS Lookup</h1>
          <p style={{ fontSize:13, color:'var(--text3)' }}>Tra cứu bản ghi DNS của tên miền bất kỳ</p>
        </div>
      </div>

      {/* Form */}
      <div className="grad-border p-5 space-y-4">
        <div>
          <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Tên miền (Domain)</label>
          <div style={{ display:'flex', gap:8 }}>
            <input className="inp" style={{ flex:1 }} placeholder="example.com"
              value={domain} onChange={e => setDomain(e.target.value)}
              onKeyDown={e => e.key==='Enter' && lookup()} />
            <button className="btn btn-ghost" onClick={() => { setDomain(''); setResults([]) }} title="Xóa">
              <RotateCcw size={14} />
            </button>
          </div>
          {/* Quick domains */}
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
            {QUICK.map(q => (
              <button key={q} onClick={() => setDomain(q)}
                style={{ fontSize:12, color:'var(--text3)', background:'var(--panel)', border:'1px solid var(--border)',
                  borderRadius:6, padding:'3px 10px', cursor:'pointer', transition:'all .15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.color='var(--accent)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--text3)'; }}>
                {q}
              </button>
            ))}
          </div>
        </div>

        {/* Record type selector */}
        <div>
          <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:8 }}>Loại bản ghi</label>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {TYPES.map(t => {
              const active = types.includes(t)
              const c = TYPE_COLORS[t] || '#38bdf8'
              return (
                <button key={t} onClick={() => toggle(t)}
                  style={{ padding:'4px 14px', borderRadius:20, fontSize:13, fontWeight:700,
                    fontFamily:'JetBrains Mono,monospace',
                    cursor:'pointer', transition:'all .15s',
                    background: active ? c+'22' : 'var(--panel)',
                    color:      active ? c        : 'var(--text3)',
                    border:     active ? `1px solid ${c}55` : '1px solid var(--border)' }}>
                  {t}
                </button>
              )
            })}
          </div>
        </div>

        <button className="btn btn-primary" onClick={lookup} disabled={loading}>
          {loading
            ? <><Loader size={14} className="animate-spin" /> Đang tra cứu...</>
            : <><Search size={14} /> Tra cứu DNS</>}
        </button>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="fade-in grad-border p-5">
          <div style={{ fontSize:13, color:'var(--text3)', marginBottom:16 }}>
            Kết quả cho{' '}
            <span style={{ color:'var(--accent)', fontFamily:'JetBrains Mono,monospace', fontWeight:700 }}>{queried}</span>
            {' '}—{' '}
            <span style={{ color:'var(--text)' }}>{results.length} bản ghi</span>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
            {Object.entries(grouped).map(([type, recs]) => {
              const c = TYPE_COLORS[type] || '#38bdf8'
              return (
                <div key={type}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                    <span style={{ padding:'2px 12px', borderRadius:20, fontSize:13, fontWeight:700,
                      fontFamily:'JetBrains Mono,monospace',
                      color: c, background: c+'18', border:`1px solid ${c}40` }}>
                      {type}
                    </span>
                    <span style={{ fontSize:12, color:'var(--text3)' }}>{recs.length} bản ghi</span>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {recs.map((r, i) => (
                      <div key={i} style={{ padding:'10px 14px', borderRadius:8,
                        background:'var(--bg)', border:'1px solid var(--border)',
                        fontFamily:'JetBrains Mono,monospace', fontSize:13 }}>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:16 }}>
                          <span style={{ color:'var(--text3)', whiteSpace:'nowrap' }}>{r.name}</span>
                          <span style={{ color:'var(--text3)', whiteSpace:'nowrap' }}>TTL: {r.ttl}s</span>
                          {r.priority !== undefined && (
                            <span style={{ color:'var(--warn)', whiteSpace:'nowrap' }}>P: {r.priority}</span>
                          )}
                          <span style={{ color:'var(--text)', wordBreak:'break-all', flex:1 }}>{r.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
